import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, Tag, Facebook, Instagram, Share2, MapPin, Star, Heart } from 'lucide-react';
import { motion } from 'framer-motion';
import { FacebookShareButton, TwitterShareButton, WhatsappShareButton } from 'react-share';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const MustVisitPlacesKampala = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = '10 Must-Visit Places in Kampala | TravelCord Blog';
  }, []);

  const handleShare = async (platform: string) => {
    const url = window.location.href;
    const title = "10 Must-Visit Places in Kampala";

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'instagram':
        alert('Follow us on Instagram @travelcord.guide');
        break;
      default:
        if (navigator.share) {
          try {
            await navigator.share({ title, url });
          } catch (error) {
            console.error('Error sharing:', error);
          }
        }
    }
  };

  const mustVisitPlaces = [
    {
      name: "Gaddafi National Mosque",
      description: "One of Africa's largest mosques with panoramic views of Kampala from its minaret.",
      image: "https://imgur.com/LTxFu0W.jpg"
    },
    {
      name: "Kasubi Tombs",
      description: "UNESCO World Heritage site and burial ground of Buganda kings.",
      image: "https://imgur.com/EzHnSsj.jpg"
    },
    {
      name: "Uganda Museum",
      description: "The oldest museum in East Africa showcasing Uganda's cultural heritage.",
      image: "https://imgur.com/4kwMnNu.jpg"
    },
    {
      name: "Ndere Cultural Centre",
      description: "Experience traditional music, dance, and storytelling performances.",
      image: "https://imgur.com/V4EbZrd.jpg"
    },
    {
      name: "Owino Market",
      description: "The largest market in Uganda offering everything from fresh produce to crafts.",
      image: "https://imgur.com/4kwMnNu.jpg"
    }
  ];

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white">
        <div className="absolute inset-0">
          <img 
            src="https://imgur.com/EzHnSsj.jpg"
            alt="Kampala City"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Link to="/blog" className="inline-flex items-center text-white/90 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Blog
            </Link>
          </div>
          
          <div className="max-w-3xl">
            <div className="flex items-center mb-4 space-x-4">
              <img 
                src="https://imgur.com/lCTURxX.jpg"
                alt="Sarah Namukasa"
                className="h-12 w-12 rounded-full border-2 border-white"
              />
              <div>
                <h3 className="text-lg font-medium">Sarah Namukasa</h3>
                <p className="text-white/80 text-sm">City Guide</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="inline-flex items-center bg-ta-green/20 rounded-full px-2.5 py-0.5 text-xs text-ta-green mb-4 backdrop-blur-sm">
                City Guide
              </div>
              
              <h1 className="text-4xl font-bold">10 Must-Visit Places in Kampala</h1>
              
              <div className="flex flex-wrap gap-4 text-sm text-white/80">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  March 20, 2025
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  7 min read
                </div>
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  Kampala
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="prose max-w-none">
                <p className="lead text-lg text-gray-600">
                  Kampala, Uganda's vibrant capital, is a city of contrasts where modern skyscrapers stand alongside historical sites and bustling markets. Whether you're a first-time visitor or a returning traveler, these ten must-visit places offer a comprehensive experience of the city's rich culture, history, and energy.
                </p>

                {mustVisitPlaces.map((place, index) => (
                  <div key={index} className="my-8">
                    <img 
                      src={place.image}
                      alt={place.name}
                      className="rounded-lg w-full mb-4"
                    />
                    <h2>{place.name}</h2>
                    <p>{place.description}</p>
                  </div>
                ))}

                <h2>More Places to Explore</h2>
                <ul>
                  <li><strong>Bahai Temple</strong> - The only Bahai temple in Africa, offering peaceful gardens and stunning architecture.</li>
                  <li><strong>Uganda Martyrs Shrine</strong> - An important religious site commemorating the martyrs of Uganda.</li>
                  <li><strong>Kabaka's Palace</strong> - The historic palace of the Buganda king.</li>
                  <li><strong>Independence Monument</strong> - A symbol of Uganda's independence from British colonial rule.</li>
                  <li><strong>Makerere University</strong> - One of Africa's oldest and most prestigious universities.</li>
                </ul>

                <div className="bg-gray-50 p-6 rounded-lg my-8">
                  <h3 className="text-lg font-semibold mb-4">Local Tips</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Visit the Gaddafi Mosque early in the morning for the best views and fewer crowds</span>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Use bodabodas (motorcycle taxis) for quick transportation, but always negotiate the price first</span>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Try local street food like rolex (chapati with eggs) from vendors around the city</span>
                    </li>
                    <li className="flex items-start">
                      <Star className="h-5 w-5 text-ta-green mr-2 flex-shrink-0" />
                      <span>Visit markets in the morning when they're less crowded and produce is freshest</span>
                    </li>
                  </ul>
                </div>

                <h2>Getting Around Kampala</h2>
                <p>
                  Navigating Kampala can be challenging due to traffic congestion, but there are several options:
                </p>
                <ul>
                  <li><strong>Bodabodas (motorcycle taxis)</strong> - Fast but require caution</li>
                  <li><strong>Special hire taxis</strong> - More comfortable but more expensive</li>
                  <li><strong>Matatus (minibus taxis)</strong> - Affordable but often crowded</li>
                  <li><strong>Ride-sharing apps</strong> - Convenient and increasingly popular</li>
                </ul>

                <p>
                  For the best experience, consider hiring a local guide who can navigate the city efficiently and provide cultural context to enhance your visit.
                </p>
              </div>

              {/* Tags */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm text-gray-600 mr-2">Tags:</span>
                  {["Kampala", "City Guide", "Uganda Travel", "Cultural Sites", "Urban Exploration"].map((tag, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-ta-green hover:text-white transition-colors cursor-pointer"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Share Buttons */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold mb-4">Share this article</h3>
                <div className="flex space-x-4">
                  <FacebookShareButton
                    url={window.location.href}
                    quote="10 Must-Visit Places in Kampala"
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Facebook className="h-5 w-5 mr-2" />
                    Facebook
                  </FacebookShareButton>
                  <TwitterShareButton
                    url={window.location.href}
                    title="10 Must-Visit Places in Kampala"
                    className="flex items-center px-4 py-2 bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    Twitter
                  </TwitterShareButton>
                  <WhatsappShareButton
                    url={window.location.href}
                    title="10 Must-Visit Places in Kampala"
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Share2 className="h-5 w-5 mr-2" />
                    Share
                  </WhatsappShareButton>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Author Card */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center mb-4">
                    <img 
                      src="https://imgur.com/lCTURxX.jpg"
                      alt="Sarah Namukasa"
                      className="h-16 w-16 rounded-full"
                    />
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold">Sarah Namukasa</h3>
                      <p className="text-gray-600">City Guide</p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">
                    Sarah is a Kampala native with extensive knowledge of the city's hidden treasures 
                    and cultural spots. She specializes in city tours and cultural experiences.
                  </p>
                  <button className="w-full py-2 bg-ta-green text-white rounded-lg hover:bg-ta-green-dark transition-colors">
                    Follow
                  </button>
                </div>

                {/* Table of Contents */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">Table of Contents</h3>
                  <nav className="space-y-2">
                    <a href="#gaddafi-mosque" className="block text-gray-600 hover:text-ta-green">Gaddafi National Mosque</a>
                    <a href="#kasubi-tombs" className="block text-gray-600 hover:text-ta-green">Kasubi Tombs</a>
                    <a href="#uganda-museum" className="block text-gray-600 hover:text-ta-green">Uganda Museum</a>
                    <a href="#ndere-centre" className="block text-gray-600 hover:text-ta-green">Ndere Cultural Centre</a>
                    <a href="#owino-market" className="block text-gray-600 hover:text-ta-green">Owino Market</a>
                    <a href="#more-places" className="block text-gray-600 hover:text-ta-green">More Places</a>
                    <a href="#getting-around" className="block text-gray-600 hover:text-ta-green">Getting Around</a>
                  </nav>
                </div>

                {/* Related Articles */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold mb-4">Related Articles</h3>
                  <div className="space-y-4">
                    <Link to="/blog/hidden-gems-kampala" className="block group">
                      <div className="flex items-start">
                        <img 
                          src="https://imgur.com/EzHnSsj.jpg"
                          alt="Hidden Gems in Kampala"
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-ta-green">
                            10 Hidden Gems in Kampala You Can't Miss
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">6 min read</p>
                        </div>
                      </div>
                    </Link>
                    <Link to="/blog/kampala-food-guide" className="block group">
                      <div className="flex items-start">
                        <img 
                          src="https://imgur.com/4kwMnNu.jpg"
                          alt="Kampala Food Guide"
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="ml-3">
                          <h4 className="text-sm font-medium text-gray-900 group-hover:text-ta-green">
                            Kampala's Best Street Food Guide
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">8 min read</p>
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default MustVisitPlacesKampala;